import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import {
    IProductItemViewModel,
    IWishListTranslations,
} from 'Scripts/Types/Litium';

import {
    useAddToWishListMutation,
    useRemoveFromWishListMutation,
    useRemoveFromWishListPageMutation,
} from 'Services/wishListApi';

import HeartIcon from 'Icons/heart.svg?react';

import CtaButton from './CtaButton';

interface WishListButtonProps {
    isOnWishList: IProductItemViewModel['isOnWishList'];
    enabledWishlist: IProductItemViewModel['enabledWishlist'];
    isOnWishListPage?: boolean;
    id: string;
    width?: string;
    height?: string;
    stroke?: string;
    colors?: IProductItemViewModel['productFilters']['colors'];
    values?: IProductItemViewModel['productFilters']['values'];
    additionalTranslations?: IWishListTranslations;
    name?: string;
    wishListIconText?: string;
}

const WishListButton = ({
    height,
    width,
    stroke,
    colors,
    values,
    additionalTranslations,
    name,
    isOnWishListPage = false,
    isOnWishList,
    enabledWishlist,
    id,
    wishListIconText,
}: WishListButtonProps) => {
    const [isWishListItem, setIsWishListItem] = useState(isOnWishList);
    const [
        addToWishList,
        {
            data: wishListData,
            isLoading: isAddingToWishList,
            isError: isErrorAddToWishList,
            error: errorAddToWishList,
        },
    ] = useAddToWishListMutation();

    const [
        removeFromWishList,
        {
            data: removeFromWishListData,
            isLoading: isRemovingFromWishList,
            isError: isErrorRemovingFromWishList,
            error: errorRemovingFromWishList,
        },
    ] = useRemoveFromWishListMutation();
    const [
        removeFromWishListPage,
        {
            isError: isErrorRemovingFromWishListPage,
            error: errorRemovingFromWishListPage,
        },
    ] = useRemoveFromWishListPageMutation();

    useEffect(() => {
        if (wishListData) {
            setIsWishListItem(
                (wishListData as IProductItemViewModel)?.isOnWishList
            );
        }
    }, [wishListData]);
    useEffect(() => {
        if (removeFromWishListData) {
            setIsWishListItem(
                (removeFromWishListData as IProductItemViewModel)?.isOnWishList
            );
        }
    }, [removeFromWishListData]);

    useEffect(() => {
        setIsWishListItem(isOnWishList);
    }, [isOnWishList, id]);

    const handleRemoveFromWishList = (articleNumber: string) => {
        toast(
            t => (
                <div className="flex flex-col gap-1 font-montserrat">
                    <div className="mr-7 flex flex-col gap-1">
                        <p>
                            {`${additionalTranslations.wishListPageRemoveWishlistItemConfirm} `}
                            <strong>{name}</strong>
                        </p>
                        <p>
                            {`${additionalTranslations.wishListPageArticleNumber} `}
                            <strong>{articleNumber.slice(0, 5)}</strong>
                        </p>
                        <p>
                            {`${additionalTranslations.wishListPageColor} `}
                            <strong>
                                {colors.find(x => x.isActive)?.title ??
                                    values.find(v => v.isActive)?.title}
                            </strong>
                        </p>
                    </div>
                    <div className="mt-5 flex gap-2">
                        <CtaButton
                            autoFocus
                            type="button"
                            className="flex basis-1/2 items-center justify-center"
                            ctaType="primary"
                            onClick={() => {
                                removeFromWishListPage({
                                    articleNumber,
                                });
                                toast.dismiss(t.id);
                            }}
                        >
                            {
                                additionalTranslations.wishListPageConfirmButtonLabel
                            }
                        </CtaButton>
                        <CtaButton
                            type="button"
                            className="flex basis-1/2 items-center justify-center"
                            onClick={() => toast.dismiss(t.id)}
                            ctaType="secondary"
                        >
                            {
                                additionalTranslations.wishListPageRejectButtonLabel
                            }
                        </CtaButton>
                    </div>
                </div>
            ),
            {
                duration: Infinity,
                style: {
                    background: '#ffffff',
                },
            }
        );
    };

    const handleWishListActions = (articleNumber: string) => {
        if (enabledWishlist) {
            if (isWishListItem && !isOnWishListPage) {
                removeFromWishList({
                    articleNumber,
                });
            } else if (isOnWishListPage) {
                handleRemoveFromWishList(articleNumber);
            } else {
                addToWishList({
                    articleNumber,
                });
            }
        } else {
            toast.error(additionalTranslations.wishListNotEnabledToastMessage);
        }
    };
    useEffect(() => {
        if (isErrorAddToWishList && 'data' in errorAddToWishList) {
            const errMessage: { message?: string } = errorAddToWishList.data;
            toast.error(errMessage.message);
        }
    }, [errorAddToWishList, isErrorAddToWishList]);

    useEffect(() => {
        if (
            isErrorRemovingFromWishList &&
            'data' in errorRemovingFromWishList
        ) {
            const errMessage: { message?: string } =
                errorRemovingFromWishList.data;
            toast.error(errMessage.message);
        }
    }, [errorRemovingFromWishList, isErrorRemovingFromWishList]);

    useEffect(() => {
        if (
            isErrorRemovingFromWishListPage &&
            'data' in errorRemovingFromWishListPage
        ) {
            const errMessage: { message?: string } =
                errorRemovingFromWishListPage.data;
            toast.error(errMessage.message);
        }
    }, [errorRemovingFromWishListPage, isErrorRemovingFromWishListPage]);
    return (
        <button
            disabled={isRemovingFromWishList || isAddingToWishList}
            type="button"
            className="cursor-pointer"
            onClick={() => handleWishListActions(id)}
        >
            <HeartIcon
                style={
                    {
                        '--icon-width': width,
                        '--icon-height': height,
                        '--icon-stroke': stroke,
                    } as React.CSSProperties
                }
                className={`h-5 w-5 ${
                    stroke ? '[stroke-width:var(--icon-stroke)]' : 'stroke-2'
                } ${width ? 'lg:w-[var(--icon-width)]' : 'lg:w-7'} ${
                    height ? 'lg:h-[var(--icon-height)]' : 'lg:h-6'
                } ${isWishListItem ? 'fill-black' : 'fill-none'} `}
            />
            <span className="sr-only">{wishListIconText}</span>
        </button>
    );
};

export default WishListButton;
